/**
 * Step-up OTP API Service
 *
 * The backend sends the code to the phone on the signed-in account, so these
 * calls send no phone. Both calls throw on failure, and the OTP dialog shows
 * the error, so the global error toast is skipped.
 */

import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1/auth/step-up";

export async function sendStepUpOtp(): Promise<void> {
	await apiClient.post(`${API_BASE}/send-otp`, undefined, {
		inlineErrors: true,
	});
}

export async function verifyStepUpOtp(otp: string): Promise<void> {
	await apiClient.post(
		`${API_BASE}/verify-otp`,
		{ otp },
		{ inlineErrors: true },
	);
}

/** Seconds to wait from a 429 refusal, or undefined for another failure. */
export function getRetryAfterSeconds(error: unknown): number | undefined {
	const err = error as {
		response?: {
			status?: number;
			data?: { data?: { retryAfterSeconds?: unknown } | null };
		};
	} | null;
	if (err?.response?.status !== 429) return undefined;
	const seconds = err.response.data?.data?.retryAfterSeconds;
	return typeof seconds === "number" ? seconds : undefined;
}

/**
 * True when a failed verify says the code timed out or there is no code
 * (data.code OTP_EXPIRED or OTP_NOT_FOUND). No code the user types can match
 * then, so the user must send a new one.
 */
export function isCodeGone(error: unknown): boolean {
	const err = error as {
		response?: { data?: { data?: { code?: unknown } | null } };
	} | null;
	const code = err?.response?.data?.data?.code;
	return code === "OTP_EXPIRED" || code === "OTP_NOT_FOUND";
}
